import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { Plus, Trash2, KeyRound, Copy, Calendar, Building2, ShieldCheck, Check, Search, ExternalLink, RefreshCw, Clock, AlertCircle } from 'lucide-react';

export default function ApiKeys() {
  const { api } = useAuth();
  const [panels, setPanels] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selPanel, setSelPanel] = useState('');
  const [modal, setModal] = useState(false);
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState('30'); // days
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [delLoad, setDelLoad] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [autoRotate, setAutoRotate] = useState(false);
  const [regenLoad, setRegenLoad] = useState(null);

  const loadPanels = useCallback(async () => {
    try {
      const res = await api.get('/superadmin/panels');
      setPanels(res.data);
      if (res.data.length > 0 && !selPanel) {
        setSelPanel(String(res.data[0].id));
      }
    } catch (err) {
      toast.error('Failed to load panels');
    }
  }, [api, selPanel]);

  const loadKeys = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await api.get(`/superadmin/panels/${pid}/api-keys`);
      setApiKeys(res.data);
    } catch (err) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadPanels(); }, [loadPanels]);
  useEffect(() => { if (selPanel) loadKeys(selPanel); }, [selPanel, loadKeys]);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/superadmin/api-keys', {
        label,
        expires_in_days: expiry,
        panel_id: selPanel,
        is_auto_rotate: autoRotate
      });
      setNewKey(res.data.api_key);
      toast.success('API Key generated');
      setLabel('');
      loadKeys(selPanel);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async (key) => {
    if (!window.confirm(`Are you sure you want to regenerate "${key.label}"? The old key will stop working immediately.`)) return;
    setRegenLoad(key.id);
    try {
      const res = await api.post(`/superadmin/api-keys/${key.id}/regenerate`);
      setNewKey(res.data.api_key);
      setModal(true); // Open modal to show the new key
      loadKeys(selPanel);
    } catch (err) {
      toast.error('Failed to regenerate key');
    } finally {
      setRegenLoad(null);
    }
  };

  const confirmDelete = async () => {
    setDelLoad(true);
    try {
      await api.delete(`/superadmin/api-keys/${deleting.id}`);
      toast.success('API Key deleted');
      setDeleting(null);
      loadKeys(selPanel);
    } catch {
      toast.error('Error');
    } finally {
      setDelLoad(false);
    }
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copied to clipboard');
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Panel API Keys</h1>
          <p className="page-subtitle">Generate secure keys for external integrations with your panels.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setModal(true); setNewKey(''); }}><Plus size={15} /> Create API Key</button>
      </div>

      <div className="flex items-center gap-4 mb-8 bg-card p-4 rounded-xl border border-border shadow-sm">
        <label className="text-sm font-bold flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
          <Building2 size={16} className="text-muted" /> Select Panel:
        </label>
        <select className="form-select" style={{ maxWidth: 300 }} value={selPanel} onChange={e => setSelPanel(e.target.value)}>
          {panels.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex-center" style={{ minHeight: 200 }}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
      ) : (
        <div className="grid-2">
          {apiKeys.map(key => {
            const isExpired = key.expires_at && new Date(key.expires_at) < new Date();
            return (
              <div key={key.id} className={`card relative overflow-hidden transition-all hover:shadow-md group ${isExpired ? 'border-danger/30' : 'hover:border-brand/50'}`}>
                {/* Status Indicator Bar */}
                <div className={`absolute top-0 left-0 right-0 h-1 ${isExpired ? 'bg-danger' : 'bg-brand'}`} />
                
                <div className="card-pad">
                  <div className="flex justify-between items-start mb-5">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center  ${isExpired ? 'bg-danger/10 text-danger' : 'bg-brand/10 text-brand'}`}>
                        <KeyRound size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-base">{key.label}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`badge ${isExpired ? 'badge-inactive' : 'badge-active'}`} style={{ fontSize: '0.65rem' }}>
                            {isExpired ? 'Expired' : 'Active'}
                          </span>
                          {key.is_auto_rotate === 1 && (
                            <span className="badge badge-panel" style={{ fontSize: '0.65rem' }}>Auto-Rotate</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      className="btn btn-ghost btn-sm btn-icon text-danger opacity-0 group-hover:opacity-100 transition-opacity" 
                      onClick={() => setDeleting(key)}
                      title="Revoke Key"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <div className="bg-bg-base p-4 rounded-xl border border-border font-mono text-xs flex items-center justify-between">
                        <div className="truncate text-text-secondary mr-2">
                          {key.api_key.substring(0, 16)}••••••••••••••••
                        </div>
                        <button className="btn btn-ghost btn-sm btn-icon text-brand flex-shrink-0" onClick={() => copyToClipboard(key.api_key)} title="Copy Key">
                          <Copy size={13} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-[11px] text-text-muted border-t border-border pt-4">
                      <div className="flex flex-col gap-1">
                        <span className="uppercase tracking-wider font-bold opacity-70">Created On</span>
                        <span className="text-text-primary font-medium">{new Date(key.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="uppercase tracking-wider font-bold opacity-70">Expires On</span>
                        <span className={`font-medium ${isExpired ? 'text-danger' : 'text-text-primary'}`}>
                          {key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'Permanent'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button 
                      className={`btn w-full justify-center gap-2 ${isExpired ? 'btn-danger' : 'btn-secondary'}`}
                      onClick={() => regenerate(key)} 
                      disabled={regenLoad === key.id}
                      style={{ borderRadius: 'var(--r-md)' }}
                    >
                      {regenLoad === key.id ? (
                        <span className="spinner" style={{ width: 14, height: 14 }} />
                      ) : (
                        <RefreshCw size={14} className={key.is_auto_rotate ? 'animate-spin-slow' : ''} />
                      )}
                      <span>Regenerate API Key</span>
                    </button>
                    {key.last_rotated_at && (
                      <p className="text-center text-[10px] text-text-muted mt-2 italic">
                        Last rotated: {new Date(key.last_rotated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {!apiKeys.length && (
            <div className="empty-state" style={{ gridColumn: '1/-1' }}>
              <KeyRound size={40} className="text-muted opacity-30" />
              <h3>No API Keys generated</h3>
              <p>Create a key to enable external access for this panel.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Key Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Generate New API Key">
        {newKey ? (
          <div className="modal-body text-center py-6">
            <div className="mb-4 inline-flex p-4 bg-success/10 rounded-full text-success">
              <Check size={32} />
            </div>
            <h3 className="text-xl font-bold mb-2">Key Generated Successfully</h3>
            <p className="text-muted text-sm mb-6">Make sure to copy your API key now. You won't be able to see it again!</p>

            <div className="bg-muted p-4 rounded-xl border-2 border-dashed border-success/30 flex items-center justify-between gap-4 mb-6">
              <code className="text-sm font-bold text-success break-all">{newKey}</code>
              <button className="btn btn-success btn-icon flex-shrink-0" onClick={() => copyToClipboard(newKey)}>
                <Copy size={16} />
              </button>
            </div>

            <button className="btn btn-primary w-full" onClick={() => setModal(false)}>I've copied the key</button>
          </div>
        ) : (
          <form onSubmit={save}>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Key Label <span className="req">*</span></label>
                <input className="form-input" placeholder="e.g. My Integration Key" value={label} onChange={e => setLabel(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Expiration</label>
                <select className="form-select" value={expiry} onChange={e => setExpiry(e.target.value)}>
                  <option value="7">7 Days</option>
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                  <option value="">Never (Permanent)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoRotate} onChange={e => setAutoRotate(e.target.checked)} />
                  <span className="text-sm font-bold">Auto-Rotate Key on Expiration</span>
                </label>
                <p className="text-xs text-muted mt-1">If enabled, the system will automatically generate a new key and allow it when the current one expires.</p>
              </div>
              <div className="p-4 bg-brand/5 border border-brand/20 rounded-lg">
                <p className="text-xs text-brand flex items-start gap-2">
                  <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
                  API keys grant full programmatic access to this panel's modules. Treat them as securely as your password.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner" /> : 'Generate Key'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={delLoad}
        title="Revoke API Key"
        message={`Are you sure you want to revoke "${deleting?.label}"? Any systems using this key will immediately lose access.`}
      />
    </>
  );
}
